import React, { useRef } from 'react';
import { View, StyleSheet, Pressable, ViewStyle, Animated } from 'react-native';
import { useTheme, SPACING, BORDER_RADIUS } from '../../theme';
import { SPRING_CONFIG_SNAPPY } from '@hooks/useAnimations';
import { useWebHover } from '../../hooks/useWebHover';
import { CURSOR_POINTER } from '../../utils/webStyles';

interface CardProps {
  children: React.ReactNode;
  variant?: 'elevated' | 'outlined' | 'filled' | 'paper';
  padding?: 'none' | 'sm' | 'md' | 'lg';
  onPress?: () => void;
  style?: ViewStyle;
  disabled?: boolean;
  testID?: string;
}

export function Card({
  children,
  variant = 'elevated',
  padding = 'md',
  onPress,
  style,
  disabled = false,
  testID,
}: CardProps) {
  const { colors } = useTheme();
  const scale = useRef(new Animated.Value(1)).current;
  const { hovered, hoverProps } = useWebHover(Boolean(onPress) && !disabled);

  const handlePressIn = () => {
    if (!onPress || disabled) return;
    Animated.spring(scale, { toValue: 0.98, ...SPRING_CONFIG_SNAPPY }).start();
  };

  const handlePressOut = () => {
    if (!onPress || disabled) return;
    Animated.spring(scale, { toValue: 1, ...SPRING_CONFIG_SNAPPY }).start();
  };

  // In-page cards are bordered, never shadowed — a 0.05-opacity shadow on a
  // white page is invisible chrome. Shadows belong to genuinely floating
  // surfaces (sheets, dropdowns, map controls). 'elevated' and 'paper' are
  // kept as aliases of the bordered treatment so call sites read naturally.
  const variantStyles: Record<NonNullable<CardProps['variant']>, ViewStyle> = {
    elevated: {
      backgroundColor: colors.surfaceElevated,
      borderWidth: 1,
      borderColor: colors.border,
    },
    outlined: {
      backgroundColor: colors.surfaceElevated,
      borderWidth: 1,
      borderColor: colors.border,
    },
    filled: {
      backgroundColor: colors.surface,
    },
    paper: {
      backgroundColor: colors.surfaceElevated,
      borderWidth: 1,
      borderColor: colors.borderLight,
    },
  };

  const paddingStyles = {
    none: {},
    sm: { padding: SPACING.sm },
    md: { padding: SPACING.md },
    lg: { padding: SPACING.lg },
  };

  const cardStyle = [
    styles.base,
    variantStyles[variant],
    hovered && { backgroundColor: colors.hoverSurface },
    paddingStyles[padding],
    disabled && styles.disabled,
    style,
  ];

  const cardContent = <>{children}</>;

  if (onPress) {
    return (
      <Animated.View style={{ transform: [{ scale }] }}>
        <Pressable
          onPress={onPress}
          onPressIn={handlePressIn}
          onPressOut={handlePressOut}
          disabled={disabled}
          accessibilityRole="button"
          accessibilityState={{ disabled }}
          testID={testID}
          {...hoverProps}
          style={[cardStyle, !disabled && CURSOR_POINTER]}>
          {cardContent}
        </Pressable>
      </Animated.View>
    );
  }

  return (
    <View style={cardStyle} testID={testID}>
      {cardContent}
    </View>
  );
}

const styles = StyleSheet.create({
  base: {
    borderRadius: BORDER_RADIUS.lg,
    overflow: 'hidden',
  },
  disabled: {
    opacity: 0.5,
  },
});
